import { useState } from "react";
import { subscriptionService, taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useAssignmentTemplate } from "@miplatform/hooks";
import { createUUID } from "@miplatform/utils";

const getIssueTemplateUUID = createUUID();
const getCreateIssueTemplateUUID = createUUID();
const deleteTemplateUUID = createUUID();
const getTradesUUID = createUUID();

export const useIssueTemplateViewModel = () => {
  const stateManager = useComponentState();
  const [issueTemplates, setIssueTemplates] = useState([]);
  const [trades, setTrades] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { assignmentTemplates, getAssignmentTemplates } = useAssignmentTemplate({
    service: taskService,
  });

  const getIssueTemplates = () => {
    stateManager.abort(getIssueTemplateUUID);
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.getAll({
        sort: "name asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setIssueTemplates(response.data);
      }
    }, getIssueTemplateUUID);
  };

  const getTrades = () => {
    stateManager.abort(getTradesUUID);
    stateManager.run(async () => {
      const response = await subscriptionService.trades.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        setTrades(response.data);
      }
    }, getTradesUUID);
  };

  const createIssueTemplate = (data) => {
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.create({
        data,
        toastMessageConfig: {
          success: {
            text: ` ${data.name} was created successfully.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.CREATED) {
        setOpenAddModal(false);
        getIssueTemplates();
      }
    }, getCreateIssueTemplateUUID);
  };

  const deleteIssueTemplate = (id, data) => {
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.delete({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: ` ${data.name} was deleted successfully.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        getIssueTemplates();
        setSelectedItem(null);
        setOpenDeleteModal(false);
      }
    }, deleteTemplateUUID);
  };

  const onCreate = (data) => {
    createIssueTemplate(data);
  };

  const onDelete = () => {
    deleteIssueTemplate(selectedItem.id, {
      modifiedOn: selectedItem.modifiedOn,
      name: selectedItem.name,
    });
  };

  const onDeleteCancel = () => {
    setOpenDeleteModal(false);
    setSelectedItem(null);
  };

  return {
    stateManager,
    issueTemplates,
    getIssueTemplates,
    openAddModal,
    setOpenAddModal,
    createIssueTemplate,
    openDeleteModal,
    setOpenDeleteModal,
    deleteIssueTemplate,
    selectedItem,
    setSelectedItem,
    onCreate,
    onDelete,
    onDeleteCancel,
    assignmentTemplates,
    getAssignmentTemplates,
    getTrades,
    trades,
  };
};
